import React from 'react';
import { Avatar, AvatarGroup, Box, Typography } from '@mui/material';

interface Rater {
  id: string;
  profilePicture?: string;
  displayName?: string;
}

interface AvatarClusterProps {
  raters: Rater[];
  totalCount: number;
}

const MAX_VISIBLE = 3;

/**
 * AvatarCluster — overlapping avatar stack (max 3) with a count label
 * showing social proof engagement (e.g., "12 people rated this 🔥 today").
 */
const AvatarCluster: React.FC<AvatarClusterProps> = ({ raters, totalCount }) => {
  if (totalCount === 0) return null;

  const visibleRaters = raters.slice(0, MAX_VISIBLE);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <AvatarGroup
        max={MAX_VISIBLE + 1}
        sx={{
          '& .MuiAvatar-root': {
            width: 24,
            height: 24,
            fontSize: '0.65rem',
            border: '2px solid',
            borderColor: 'background.paper',
          },
        }}
      >
        {visibleRaters.map((rater) => (
          <Avatar
            key={rater.id}
            src={rater.profilePicture}
            alt={rater.displayName || 'User'}
            sx={{ width: 24, height: 24 }}
          />
        ))}
      </AvatarGroup>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
      >
        {totalCount} {totalCount === 1 ? 'person' : 'people'} rated this 🔥
      </Typography>
    </Box>
  );
};

export default AvatarCluster;
